import {
  ALL_ACCEPTED_PREFERENCES,
  COOKIE_CONSENT_VERSION,
  DEFAULT_PREFERENCES,
  type CookieCategory,
  type CookieConsentRecord,
  type CookiePreferences,
} from '@/types/cookieConsent'

const STORAGE_KEY = 'sp-cookie-consent'

type ConsentListener = (record: CookieConsentRecord) => void

const listeners = new Set<ConsentListener>()

function notifyListeners(record: CookieConsentRecord) {
  listeners.forEach((listener) => listener(record))
}

function parseLegacyConsent(raw: string): CookieConsentRecord | null {
  const consentDate = localStorage.getItem('cookieConsentDate') ?? new Date().toISOString()

  if (raw === 'all') {
    return {
      hasConsented: true,
      preferences: ALL_ACCEPTED_PREFERENCES,
      consentDate,
      version: COOKIE_CONSENT_VERSION,
    }
  }

  if (raw === 'essential' || raw === 'rejected') {
    return {
      hasConsented: true,
      preferences: DEFAULT_PREFERENCES,
      consentDate,
      version: COOKIE_CONSENT_VERSION,
    }
  }

  if (raw.includes(',')) {
    const ids = raw.split(',')
    return {
      hasConsented: true,
      preferences: {
        essential: true,
        performance: ids.includes('analytics') || ids.includes('performance'),
        functional: ids.includes('preferences') || ids.includes('functional'),
        marketing: ids.includes('marketing'),
      },
      consentDate,
      version: COOKIE_CONSENT_VERSION,
    }
  }

  return null
}

export function loadCookieConsent(): CookieConsentRecord | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored) as CookieConsentRecord
      if (parsed?.hasConsented && parsed.preferences?.essential === true) {
        return parsed
      }
    }

    const legacy = localStorage.getItem('cookieConsent')
    if (legacy) {
      const migrated = parseLegacyConsent(legacy)
      if (migrated) {
        saveCookieConsent(migrated.preferences, { notify: false })
        localStorage.removeItem('cookieConsent')
        localStorage.removeItem('cookieConsentDate')
        return migrated
      }
    }
  } catch {
    return null
  }

  return null
}

export function saveCookieConsent(
  preferences: CookiePreferences,
  options: { notify?: boolean } = {}
): CookieConsentRecord {
  const record: CookieConsentRecord = {
    hasConsented: true,
    preferences: { ...preferences, essential: true },
    consentDate: new Date().toISOString(),
    version: COOKIE_CONSENT_VERSION,
  }

  localStorage.setItem(STORAGE_KEY, JSON.stringify(record))

  if (options.notify !== false) {
    notifyListeners(record)
  }

  return record
}

export function clearCookieConsent(): void {
  localStorage.removeItem(STORAGE_KEY)
}

export function subscribeToCookieConsent(listener: ConsentListener): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function isCategoryConsented(category: CookieCategory): boolean {
  if (category === 'essential') return true

  const record = loadCookieConsent()
  if (!record?.hasConsented) return false

  return record.preferences[category]
}

/** Hook point for future script loaders (GA, GTM, Meta Pixel, Clarity, etc.) */
export function applyConsentedScripts(preferences: CookiePreferences): void {
  if (typeof window === 'undefined') return

  window.dispatchEvent(
    new CustomEvent('sp:cookie-consent', {
      detail: preferences,
    })
  )

  // Future integrations:
  // if (preferences.performance) loadGoogleAnalytics()
  // if (preferences.marketing) loadMetaPixel()
}
